import { useEffect, useRef, useState } from 'react';

/**
 * Observes elements inside a container which have a targetid data attribute
 * and determines which elements are overflowing the container, returning a
 * state variable containing a map of targetid to visibility
 * @returns the containerRef and the visibilityMap
 */
export function useOverflowItems() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const attachIntersectionObserver = () => {
      if (!containerRef.current)
        return () => {
          /* */
        };
      const observer = new IntersectionObserver(
        (entries) => {
          const updatedEntries: Record<string, boolean> = {};
          entries.forEach((entry) => {
            if (entry.target instanceof HTMLElement) {
              const targetId = getDataTargetId(entry.target);
              if (targetId) {
                if (entry.isIntersecting) {
                  updatedEntries[targetId] = true;
                } else {
                  updatedEntries[targetId] = false;
                }
              }
            }
          });
          setVisibilityMap((prev) => ({
            ...prev,
            ...updatedEntries,
          }));
        },
        {
          root: containerRef.current,
          threshold: 1,
        },
      );
      Array.from(containerRef.current.children).forEach((item) => {
        if (item instanceof HTMLElement && getDataTargetId(item)) {
          observer.observe(item);
        }
      });
      return () => observer.disconnect();
    };

    let disconnectIntersectionObserver = attachIntersectionObserver();

    const attachMutationObserver = () => {
      if (!containerRef.current)
        return () => {
          /* */
        };
      const observer = new MutationObserver((mutations) => {
        disconnectIntersectionObserver();
        disconnectIntersectionObserver = attachIntersectionObserver();
      });
      observer.observe(containerRef.current, { childList: true });

      return () => observer.disconnect();
    };

    const disconnectMutationObserver = attachMutationObserver();
    return () => {
      disconnectIntersectionObserver();
      disconnectMutationObserver();
    };
  }, []);

  return {
    containerRef,
    visibilityMap,
  };
}

function getDataTargetId(node: HTMLElement) {
  return node.dataset?.['targetid'];
}
